import React, { useState, useEffect } from "react";
import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import AppIcon from "@/components/AppIcon";
import AppButton from "@/components/AppButton";

interface DynamicInputListProps {
  labelPrefix: string; // Label prefix like "Alert Email" or "Phone Number"
  maxInputs: number; // Maximum number of input fields
  initialValues?: { id?: number; value: string }[]; // Initial values for inputs
  onInputChange?: (values: { id?: number; value: string }[]) => void; // Callback when saving
  onDelete?: (id: number | undefined) => Promise<void>; // Optional delete handler for existing fields
}

const DynamicInputList: React.FC<DynamicInputListProps> = ({
  labelPrefix,
  maxInputs,
  initialValues = [],
  onInputChange,
  onDelete,
}) => {
  const [inputList, setInputList] = useState<{ id?: number; value: string }[]>(
    initialValues
  );

  useEffect(() => {
    if (initialValues.length) {
      setInputList(initialValues);
    }
  }, [initialValues]);

  const handleAddField = () => {
    if (inputList.length < maxInputs) {
      setInputList([...inputList, { value: "" }]);
    }
  };

  const handleRemoveField = async (id: number | undefined, index: number) => {
    if (id && onDelete) {
      await onDelete(id);
    }

    const updatedList = inputList.filter((_, i) => i !== index);
    setInputList(updatedList);

    if (onInputChange) {
      onInputChange(updatedList);
    }
  };

  const handleInputChange = (value: string, index: number) => {
    const updatedList = [...inputList];
    updatedList[index] = { ...updatedList[index], value };
    setInputList(updatedList);

    if (onInputChange) {
      onInputChange(updatedList);
    }
  };

  const handleBlur = (item: any, index: number) => {
    if (item.value == '') {
      handleRemoveField(undefined, index)
    }
  }

  return (
    <AppBox column gap={2}>
      {inputList.map((item, index) => (
        <AppBox key={index} gap={2} align="end" className="relative">
          <AppBox fullWidth justify="between" gap={2}>
            <AppInput
              type="text"
              fullWidth
              label={`${labelPrefix} #${index + 1}`}
              value={item.value}
              onChange={(value) => handleInputChange(value, index)}
              endAdornment={index > 0 && (
                <AppIcon
                  name="trash"
                  size={18}
                  onClick={() => handleRemoveField(item.id, index)}
                  className="cursor-pointer text-red-500 hover:text-red-700"
                />
              )}
              onBlur={() => handleBlur(item, index)}
            />
          </AppBox>
        </AppBox>
      ))}

      {/* Add Button */}
      {inputList.length < maxInputs && (
        <AppButton variant="text" onClick={handleAddField}>
          + Add {labelPrefix}
        </AppButton>
      )}
    </AppBox>
  );
};

export default DynamicInputList;